import { useEffect, useState } from 'react';
import { getMermaid } from '../lib/mermaid';

let renderSeq = 0;

/**
 * Renders a ```mermaid fence from user/agent-authored markdown as a diagram.
 *
 * Unlike the docs MermaidDiagram, the chart text here is untrusted (issue
 * bodies, PM plan comments), so it rides on the shared loader's strict
 * securityLevel and degrades gracefully: while loading and on any parse
 * failure (e.g. a truncated diagram), the raw source is shown as a plain code
 * block instead of an error or a blank hole.
 */
export function MermaidBlock({ chart }: { chart: string }) {
  const [svg, setSvg] = useState('');
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    setSvg('');
    getMermaid()
      .then(async (mermaid) => {
        renderSeq += 1;
        const id = `mmd-block-${renderSeq}`;
        try {
          const { svg: rendered } = await mermaid.render(id, chart);
          if (!cancelled) setSvg(rendered);
        } catch (err) {
          // Mermaid can leave scratch elements in the DOM on a parse error —
          // it works in a temp element whose id is the render id prefixed with 'd'.
          document.getElementById(id)?.remove();
          document.getElementById(`d${id}`)?.remove();
          throw err;
        }
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [chart]);

  if (!svg || failed) {
    return (
      <pre data-testid="mermaid-source">
        <code>{chart}</code>
      </pre>
    );
  }

  return (
    <div
      data-testid="mermaid-block"
      role="img"
      className="mermaid-diagram my-3 flex justify-center overflow-x-auto rounded-lg border border-line bg-surface-1 px-4 py-5"
      // svg is Mermaid's output for the chart, sanitized by securityLevel 'strict'
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}
